import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { Alert } from "react-bootstrap";
import type { MappingData } from "../../../../api/src/lib/dataMapping";
import type { Household } from "../../../../api/src/households-base/households-base.service";
import type { HouseholdsController } from "../../../../api/src/households/households.controller";
import type { UnpackResponse } from "../../../../api/src/lib";
import type { TargetMappingData } from "../../../../api/src/lib/dataMapping";
import type { ProfileController } from "../../../../api/src/profile/profile.controller";
import Content from "../../components/Content";
import DataMappingModal from "../../components/DataMapping";
import { useErrorMessage } from "../../components/FormError";
import SubmitButton from "../../components/SubmitButton";
import HouseholdMappingReview from "./mapping-review/HouseholdMappingReview";
import {
  refusalSentence,
  useAuthenticatedFetch,
  useAuthenticatedMutationAsync,
} from "../../lib/api";

type LinkData = Record<string, Record<string, string | number>>;

// The text in place of the two lists while no CRM is connected (R8, APP-2031).
const NO_CRM_CONNECTED =
  "No CRM is connected. Connect Wealthbox, Redtail or Slant to link households to CRM records.";

// The Auto-Match suggestions of a CRM are read from this URL plus the CRM
// name. `useAuthenticatedFetch` uses the URL as the query key.
const SUGGESTIONS_URL = "/v1/households/data/mappings/suggestions/";

const HouseholdMapping = () => {
  const {
    isPending: isPendingProfile,
    isSuccess: isSuccessProfile,
    isError: isErrorProfile,
    data: dataProfile,
  } = useAuthenticatedFetch<UnpackResponse<ProfileController["getProfile"]>>(
    "/profile",
  );
  const user = dataProfile?.data;

  const { errorMessage, setError, resetError } = useErrorMessage();

  const [service, setService] = useState("");

  const providerOptions = useMemo(
    () => [
      ...(!user?.connections.wealthbox ? [] : ["wealthbox"]),
      ...(!user?.connections.redtail ? [] : ["redtail"]),
      ...(!user?.connections.slant ? [] : ["slant"]),
    ],
    [user],
  );
  // For Slant the page is a review list (APP-1692), not `DataMappingModal`.
  // The first CRM of the list is the selected one until the advisor picks.
  const showSlantReview =
    (service !== "" ? service : (providerOptions[0] ?? "")) === "slant";
  // The profile read returned and connects no CRM: the page shows a text in
  // place of `DataMappingModal`, which would fall back to the first CRM of the
  // read, and reads no mappings (R8, APP-2031). A failed profile read is not
  // this: the modal shows its error.
  const noCrmConnected = isSuccessProfile && providerOptions.length === 0;

  const {
    isPending: isPendingMapping,
    isFetching: isFetchingMapping,
    isError: isErrorMapping,
    data: householdWealthboxMappings,
  } = useAuthenticatedFetch<
    UnpackResponse<HouseholdsController["getHouseholdMappings"]>
  >(
    "/v1/households/data/mappings",
    {},
    // The review list does not read the mappings. This read walks each CRM
    // list, the Slant one too, so it is not sent while the review list shows.
    { enabled: !isPendingProfile && !showSlantReview && !noCrmConnected },
  );

  const {
    isFetching: isFetchingSuggestions,
    data: suggestions,
    refetch: fetchSuggestions,
  } = useAuthenticatedFetch<
    UnpackResponse<HouseholdsController["getHouseholdMappingSuggestions"]>
  >(`${SUGGESTIONS_URL}${service}`, undefined, {
    enabled: false,
  });

  const onAutoSuggest = useCallback(async () => {
    resetError();

    const { isError, error } = await fetchSuggestions();
    if (isError) {
      const message = "Failed to auto-match households";
      setError(message);
      console.error(message, error);
    }
  }, [fetchSuggestions, setError, resetError]);

  const mapHouseholds = useAuthenticatedMutationAsync<
    UnpackResponse<HouseholdsController["mapHouseholds"]>
  >("/v1/households/data/mappings", async (data: LinkData) => ({
    method: "POST",
    body: JSON.stringify(data),
  }));

  const queryClient = useQueryClient();

  const { mutateAsync: onMap, isPending: isPendingOnMap } = useMutation({
    mutationFn: async (data: LinkData) => {
      resetError();

      try {
        const mappedHouseholds = await mapHouseholds(data);
        // Clear the Auto-Match suggestions of every CRM after a successful
        // save (APP-1686). The mapping table adds each suggestion to its row
        // again when the mapping data changes, so a suggestion that the
        // advisor removed from a row would come back, and the next Save would
        // remove the earlier CRM link of that household. A failed save does
        // not reach this line and keeps the suggestions. The query is not
        // enabled, so the reset does not fetch; a click on Auto-Match does.
        await queryClient.resetQueries({
          predicate: ({ queryKey: [key] }) =>
            typeof key === "string" && key.startsWith(SUGGESTIONS_URL),
        });
        queryClient.setQueryData(["/v1/households/data/mappings"], {
          data: mappedHouseholds.data,
        });
      } catch (err) {
        // A 4xx refusal shows the API `message` (R18, APP-2027).
        const message = "Failed to connect households";
        setError(refusalSentence(err) ?? message);
        console.error(message, err);
      }
    },
  });

  const handleLinkHouseholds = useCallback(
    async (
      newAssignedData: TargetMappingData<Household>[],
      unlinkProviders: string[] = [],
    ) => {
      const mappings = newAssignedData.reduce(
        (result, mappedHousehold) => ({
          ...result,
          ...Object.entries(mappedHousehold.assigned).reduce(
            (assignedResult, [provider, records]) => ({
              ...assignedResult,
              ...(records.length <= 0
                ? {}
                : {
                    [provider]: {
                      ...result[provider],
                      ...assignedResult[provider],
                      [mappedHousehold.id]: records[0].id,
                    },
                  }),
            }),
            {} as LinkData,
          ),
        }),
        {} as LinkData,
      );
      // A provider whose every arm is now empty (e.g. the user unlinked the last
      // household linked to it) is dropped by the reducer above, so its key never
      // reaches the backend and the unlink is silently skipped. Force the key to
      // exist for any provider the modal explicitly unlinked: the backend treats a
      // present-but-absent household as a delete, committing the unlink.
      for (const provider of unlinkProviders) {
        if (mappings[provider] === undefined) {
          mappings[provider] = {};
        }
      }
      await onMap(mappings);
      queryClient.invalidateQueries({
        queryKey: [`/v1/households/data/mappings`],
      });
    },
    [onMap, queryClient],
  );

  const mappingData = useMemo(
    () => ({
      ...(householdWealthboxMappings?.data ?? {
        target: [],
        source: {},
      }),
      // `GET /v1/households/data/mappings/suggestions/:service` answers
      // `{ suggested: [{ householdId, recordId }] }` (APP-1653); the mapping
      // table reads a map of household ID to record IDs, as before.
      suggested: (suggestions?.data.suggested ?? []).reduce<
        Record<number, (number | string)[]>
      >(
        (result, { householdId, recordId }) => ({
          ...result,
          [householdId]: [...(result[householdId] ?? []), recordId],
        }),
        {},
      ),
    }),
    [householdWealthboxMappings, suggestions],
  );

  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  // A change to a different CRM sets "Filter by Tag" to "All Tags" (R29), so
  // the next create sends no tag of the earlier CRM. `DataMappingModal`
  // reports the shown CRM on the first render too: a change from no CRM keeps
  // the tag.
  const [tagsService, setTagsService] = useState(service);
  if (tagsService !== service) {
    setTagsService(service);
    if (tagsService !== "") setSelectedTags([]);
  }

  const availableTags = useMemo(() => {
    const tags = new Set<string>();
    for (const item of mappingData.source[service] ?? []) {
      for (const tag of (item as MappingData).tags ?? []) {
        tags.add(typeof tag === "string" ? tag : tag.name);
      }
    }
    return Array.from(tags).sort();
  }, [mappingData.source, service]);

  const [isCreating, setIsCreating] = useState(false);

  const createNewHouseholds = useAuthenticatedMutationAsync<
    UnpackResponse<HouseholdsController["createHouseholdsFromUnmapped"]>
  >(`/v1/households/data/mappings/${service}`, async () => ({
    method: "POST",
    body: JSON.stringify({
      tags: selectedTags.length > 0 ? selectedTags : undefined,
    }),
  }));

  const onCreateNewHouseholds = useCallback(async () => {
    resetError();
    setIsCreating(true);

    try {
      // Up to 100 creates while the last response has `remaining` and
      // `created` above 0: a create that creates no record stops them (R25).
      // Each record of `notCreated` is named once by id, in the order of the
      // responses, when the creates end (R77); a failed create shows its own
      // text instead (R28).
      const MAX_BATCHES = 100;
      const notCreated = new Map<string, string>();
      let remaining = 1;
      let created = 1;
      for (let i = 0; i < MAX_BATCHES && remaining > 0 && created > 0; i++) {
        const result = await createNewHouseholds();
        remaining = result.data.remaining;
        created = result.data.created;
        queryClient.setQueryData(["/v1/households/data/mappings"], {
          data: result.data.mappings,
        });
        for (const record of result.data.notCreated ?? []) {
          const key = String(record.id);
          if (!notCreated.has(key)) notCreated.set(key, record.name);
        }
      }
      if (notCreated.size > 0) {
        setError(
          `Failed to create households for: ${[...notCreated.values()].join(", ")}.`,
        );
      }
    } catch (err) {
      const message = "Failed to create households";
      setError(message);
      console.error(message, err);
    } finally {
      setIsCreating(false);
    }
  }, [createNewHouseholds, setError, resetError, queryClient]);

  const createHouseholdsButton = (
    <SubmitButton
      label="Create from Unlinked"
      onClick={onCreateNewHouseholds}
      isSubmitting={isCreating}
      // The caller `disabled` replaces the one `SubmitButton` sets from
      // `isSubmitting`, so `isCreating` stays in it (R27, R79, R83, R84).
      disabled={
        isCreating ||
        // Each read, the one after a save or a CRM change too (R79):
        // `isPending` is true only before the first read has data.
        isPendingMapping ||
        isFetchingMapping ||
        isPendingProfile ||
        isErrorMapping ||
        isErrorProfile ||
        isPendingOnMap
      }
    />
  );

  const onSetReviewService = useCallback(
    (selectedService: string) => {
      setService(selectedService);
      // A save of the review list changes the links this read returns.
      queryClient.invalidateQueries({
        queryKey: ["/v1/households/data/mappings"],
      });
    },
    [queryClient],
  );

  if (showSlantReview) {
    return (
      <HouseholdMappingReview
        providerOptions={providerOptions}
        onSetService={onSetReviewService}
      />
    );
  }

  if (noCrmConnected) {
    return (
      <>
        <h1>Household Data Connections</h1>
        <Content>
          <Alert variant="info" className="mb-0">
            {NO_CRM_CONNECTED}
          </Alert>
        </Content>
      </>
    );
  }

  return (
    <>
      {errorMessage}
      <DataMappingModal
        linkHeader="/clients/households/"
        title="Household Data Connections"
        targetTitle="Household"
        providerOptions={providerOptions}
        initialService={service !== "" ? service : undefined}
        sourceTitle="Unlinked"
        isLoading={isPendingMapping || isPendingProfile}
        isError={isErrorMapping || isErrorProfile}
        mappingData={mappingData}
        isSubmitting={isPendingOnMap}
        handleSubmit={handleLinkHouseholds}
        onAutoSuggest={onAutoSuggest}
        onSetService={setService}
        isLoadingSuggestions={isFetchingSuggestions}
        isChangingRows={isCreating}
        oneToMany={false}
        additionalButtons={createHouseholdsButton}
        showNewRows={true}
        selectedTags={selectedTags}
        onTagFilterChange={setSelectedTags}
        availableTags={availableTags}
        enforceSingleProvider={true}
      />
    </>
  );
};

export default HouseholdMapping;
